import { Info, AlertTriangle, AlertCircle, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

interface CoachingNote {
  id: string;
  note_type: string;
  suggestion_text: string;
  severity: 'info' | 'suggestion' | 'warning' | 'critical';
  timestamp_in_call?: number;
  relevant_transcript_excerpt?: string;
  ai_confidence?: number;
}

interface CoachingNotesProps {
  notes: CoachingNote[];
  onTimestampClick?: (seconds: number) => void;
}

const NOTE_TYPE_LABELS: Record<string, string> = {
  objection_handling: 'Objection Handling',
  opportunity_identification: 'Opportunity Identified',
  closing_technique: 'Closing Technique',
  rapport_building: 'Rapport Building',
  compliance_reminder: 'Compliance',
  upsell_suggestion: 'Upsell Opportunity',
  tone_feedback: 'Tone Feedback',
  pacing_feedback: 'Pacing',
  question_quality: 'Question Quality',
  listening_skills: 'Listening Skills'
};

export function CoachingNotes({ notes, onTimestampClick }: CoachingNotesProps) {
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

  const toggleExpanded = (noteId: string) => {
    const newExpanded = new Set(expandedNotes);
    if (newExpanded.has(noteId)) {
      newExpanded.delete(noteId);
    } else {
      newExpanded.add(noteId);
    }
    setExpandedNotes(newExpanded);
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'suggestion':
        return <Lightbulb className="w-4 h-4 text-blue-400" />;
      default:
        return <Info className="w-4 h-4 text-slate-400" />;
    }
  };

  const getSeverityColors = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-900/30 border-red-500';
      case 'warning':
        return 'bg-yellow-900/30 border-yellow-500';
      case 'suggestion':
        return 'bg-blue-900/30 border-blue-500';
      default:
        return 'bg-slate-800 border-slate-700';
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (notes.length === 0) {
    return (
      <div className="bg-slate-900 rounded-lg p-6 text-center">
        <Info className="w-8 h-8 text-slate-600 mx-auto mb-2" />
        <p className="text-sm text-slate-400">No coaching notes for this call</p>
        <p className="text-xs text-slate-500 mt-1">AI analysis may still be processing</p>
      </div>
    );
  }

  // Sort by severity (critical first) then timestamp
  const sortedNotes = [...notes].sort((a, b) => {
    const severityOrder = { critical: 0, warning: 1, suggestion: 2, info: 3 };
    const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
    if (severityDiff !== 0) return severityDiff;
    return (a.timestamp_in_call || 0) - (b.timestamp_in_call || 0);
  });

  return (
    <div className="bg-slate-900 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-200">AI Coaching Insights</h3>
        <span className="text-xs text-slate-400">{notes.length} note{notes.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {sortedNotes.map((note) => {
          const isExpanded = expandedNotes.has(note.id);
          const colors = getSeverityColors(note.severity);

          return (
            <div
              key={note.id}
              className={`border-l-4 rounded-lg p-3 ${colors} transition-all`}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1">
                  {getSeverityIcon(note.severity)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-slate-200">
                        {NOTE_TYPE_LABELS[note.note_type] || note.note_type}
                      </span>
                      {note.timestamp_in_call !== undefined && onTimestampClick && (
                        <button
                          onClick={() => onTimestampClick(note.timestamp_in_call!)}
                          className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                          title="Jump to this point in recording"
                        >
                          @ {formatTime(note.timestamp_in_call)}
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-slate-300">{note.suggestion_text}</p>
                  </div>
                </div>

                {/* Expand Button */}
                {note.relevant_transcript_excerpt && (
                  <button
                    onClick={() => toggleExpanded(note.id)}
                    className="p-1 rounded hover:bg-slate-700/50 text-slate-400 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>

              {/* Expanded Content */}
              {isExpanded && note.relevant_transcript_excerpt && (
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <div className="text-xs text-slate-400 mb-1">Relevant excerpt:</div>
                  <div className="text-xs text-slate-300 bg-slate-950/50 p-2 rounded italic">
                    "{note.relevant_transcript_excerpt}"
                  </div>
                  {note.ai_confidence !== undefined && (
                    <div className="text-xs text-slate-500 mt-2">
                      Confidence: {(note.ai_confidence * 100).toFixed(0)}%
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="pt-3 border-t border-slate-800 grid grid-cols-4 gap-2 text-xs">
        <div className="text-center">
          <div className="text-slate-500">Critical</div>
          <div className="font-semibold text-red-400">
            {notes.filter(n => n.severity === 'critical').length}
          </div>
        </div>
        <div className="text-center">
          <div className="text-slate-500">Warnings</div>
          <div className="font-semibold text-yellow-400">
            {notes.filter(n => n.severity === 'warning').length}
          </div>
        </div>
        <div className="text-center">
          <div className="text-slate-500">Tips</div>
          <div className="font-semibold text-blue-400">
            {notes.filter(n => n.severity === 'suggestion').length}
          </div>
        </div>
        <div className="text-center">
          <div className="text-slate-500">Info</div>
          <div className="font-semibold text-slate-400">
            {notes.filter(n => n.severity === 'info').length}
          </div>
        </div>
      </div>
    </div>
  );
}
